import React from 'react';
import { Box, Card, CardContent, Typography, IconButton, Stack } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { CustomIcon, GroupPath } from '../../../components';

interface ActionCardProps {
    actionType: string;
    targetName: string;
    deviceName: string;
    path: string[];
    description: string;
    onEdit?: () => void;
    onDelete?: () => void;
    draggableProps?: any;
    dragHandleProps?: any;
    deviceIcon?: any;
    showPath?: boolean;
}

export const ActionCard = React.forwardRef<HTMLDivElement, ActionCardProps>((props, ref) => {
    const {
        actionType,
        targetName,
        deviceName,
        path,
        description,
        onEdit,
        onDelete,
        draggableProps,
        dragHandleProps,
        deviceIcon,
        showPath,
    } = props;
    const theme = useTheme();

    return (
        <Card
            ref={ref}
            {...draggableProps}
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                mb: 2,
                backgroundColor: theme.palette.background.paper,
                position: 'relative',
                userSelect: 'none',
                ...draggableProps?.sx,
            }}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <IconButton
                        {...dragHandleProps}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            cursor: 'grab',
                            mt: -0.5,
                            ml: -1,
                        }}
                    >
                        <DragHandleIcon fontSize="small" />
                    </IconButton>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {actionType}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        mb: 0.25,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {targetName}
                                </Typography>

                                {showPath && (
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                            <CustomIcon
                                                family={deviceIcon?.web?.family ?? ''}
                                                iconName={deviceIcon?.web?.name ?? ''}
                                                iconProps={{ sx: { fontSize: 16 } }}
                                            />
                                        </Box>
                                        <GroupPath
                                            pathName={path}
                                            deviceName={deviceName}
                                            color="text.secondary"
                                            fontWeight={500}
                                        />
                                    </Stack>
                                )}
                            </Box>

                            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                {onDelete && (
                                    <IconButton
                                        size="small"
                                        onClick={onDelete}
                                        sx={{ color: theme.palette.text.secondary }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton size="small" onClick={onEdit} sx={{ color: theme.palette.text.secondary }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {description && (
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                    mt: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
});
